import { InvestorFacade } from 'src/app/store/investor/facade/investor.facade';
import { filter } from 'rxjs/operators';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { PIRLookup } from './../../../models/lookups/PIRLookup';
import { formatIrdNumber } from './../../../utils/format-ird';
import { Observable } from 'rxjs';
import { RegistrationKiwiSaverFacade } from './../../../store/registration-kiwisaver/facade/kiwisaver-registration.facade';
import { UserFacade } from './../../../store/user/facade/user.facade';
import { AccountVM } from './../../../models/account';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IRegisterKiwiSaverUser } from './../../../models/registration/register';
import { UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { OnInit, Component, OnDestroy } from '@angular/core';
import { DATE_FORMATS } from 'src/app/models/date';

// Depending on whether rollup is used, moment needs to be imported differently.
// Since Moment.js doesn't have a default export, we normally need to import using the `* as`
// syntax. However, rollup creates a synthetic default module and we thus need to import it using
// the `default as` syntax.
import * as _moment from 'moment';
import { dateValidator } from 'src/app/utils/date-validator';

const moment = _moment;

@UntilDestroy()
@Component({
    selector: "app-kiwisaver-register",
    templateUrl: "./register-kiwisaver.component.html",
    styleUrls: ['./register-kiwisaver.component.scss'],
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS }
    ],
})
export class RegisterKiwisaverComponent implements OnInit {
    public userTitles = [
        "Miss",
        "Mr",
        "Mrs",
        "Ms"
    ];

    public pirTaxRates = PIRLookup;
    public PIRTaxRates$ = this.investorFacade.PIRTaxRateSchedule$;
    public userTitleTooltipText = 'If your preferred title does not appear in this list, please select the most appropriate option and contact us once your account has been set up, as we can change this for you.';
    public dobTooltipText = 'Please confirm your date of birth.';
    public pirTaxRateTooltipText = "A prescribed investor rate (PIR) is your tax rate used to calculate how much tax you’ll pay on income that you have earned on your investment in a portfolio investment entity (PIE).\n\n For More Info";
    public registerForm: UntypedFormGroup;
    public userTitle: UntypedFormControl;
    public firstName: UntypedFormControl;
    public lastName: UntypedFormControl;
    public email: UntypedFormControl;
    public irdNumber: UntypedFormControl;
    public pirTaxRate: UntypedFormControl;
    public dateOfBirth: UntypedFormControl;

    public isDisabled = false;

    private emailStateValue = '';
    private firstNameStateValue = '';
    private lastnameStateValue = '';
    private irdNumberStateValue = '';
    private dobStateValue: _moment.Moment;
    private formattedIrd: string;

    registerFailText$: Observable<string> = this.registrationKiwiSaverFacade.failtext$;

    constructor(
        private userFacade: UserFacade,
        private registrationKiwiSaverFacade: RegistrationKiwiSaverFacade,
        private investorFacade: InvestorFacade
    ) { }

    ngOnInit() {
        this.isDisabled = false;
        this.createFormControls();
        this.createForm();
        this.subscribeToUserProfile();
        this.registrationKiwiSaverFacade.failtext$.pipe(untilDestroyed(this)).subscribe(failtext => {
            // re-enables the register button once the error message appears
            if (failtext && failtext.length) {
                this.isDisabled = false;
            }
        });
    }

    private subscribeToUserProfile() {
        this.userFacade.userProfileWhenNotNull$.pipe(
            untilDestroyed(this)
        ).subscribe((userProfile: AccountVM) => {
            this.emailStateValue = userProfile.email;
            this.firstNameStateValue = userProfile.firstName;
            this.lastnameStateValue = userProfile.lastName;
            this.irdNumberStateValue = userProfile.taxNumber;
            // The substring to userprofile dob is to exclude any time or timezone component and just to take the date part.
            if (userProfile.dateOfBirth) {
                const dateObj = new Date(userProfile.dateOfBirth.substring(0, 10));
                this.dobStateValue = moment(dateObj);
            }
            this.updateFormValues();
        });
    }

    private createFormControls() {
        this.userTitle = new UntypedFormControl("", Validators.required);
        this.firstName = new UntypedFormControl({ value: this.firstNameStateValue, disabled: true });
        this.lastName = new UntypedFormControl({ value: this.lastnameStateValue, disabled: true });
        this.email = new UntypedFormControl({ value: this.emailStateValue, disabled: true });
        this.irdNumber = new UntypedFormControl({ value: this.irdNumberStateValue }, [Validators.required, Validators.pattern(/^\d{2,3}\-\d{3}\-\d{3}$/)]);
        this.pirTaxRate = new UntypedFormControl('', Validators.required);
        this.dateOfBirth = new UntypedFormControl('', [Validators.required, dateValidator(this.getMinimumEligibleDateOfBirth())]);
    }

    private getMinimumEligibleDateOfBirth() {
        const minimumDateOfBirth = new Date();
        minimumDateOfBirth.setFullYear(minimumDateOfBirth.getFullYear() - 100, minimumDateOfBirth.getMonth(), minimumDateOfBirth.getDay() - 1);
        return minimumDateOfBirth;
    }

    private createForm() {
        this.registerForm = new UntypedFormGroup({
            userTitle: this.userTitle,
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
            irdNumber: this.irdNumber,
            pirTaxRate: this.pirTaxRate,
            dateOfBirth: this.dateOfBirth
        });
    }

    private updateFormValues() {
        this.registerForm.patchValue({
            firstName: this.firstNameStateValue,
            lastName: this.lastnameStateValue,
            email: this.emailStateValue,
            irdNumber: this.irdNumberStateValue,
            dateOfBirth: this.dobStateValue
        });
    }

    public formatIrd(event: any) {
        this.formattedIrd = formatIrdNumber(event);
        this.irdNumber.setValue(this.formattedIrd);
    }


    onSubmit() {

        this.isDisabled = true;
        const values: Partial<IRegisterKiwiSaverUser> = {
            Title: this.userTitle.value,
            TaxNumber: this.irdNumber.value,
            TaxRate: this.pirTaxRate.value,
            DateOfBirth: this.dateOfBirth.value
        };

        this.registrationKiwiSaverFacade.registerKiwiSaver(values);
    }
}
